/* The alert-cluster card (docs/alert-clusters-refactor.md), in the
 * 2026-09-14 design.
 *
 * Tokens only — --card/--ink/--line/--soft/--faint and friends are defined
 * by outlook.css on .nw-ol and by the widget's own --nw-* set, so this file
 * inverts with the theme without knowing which theme it is in. There are no
 * literal colours anywhere in it: the severity swatches arrive inline from
 * the shared SEVERITY registry, and every tint below is mixed from a token.
 *
 * COLOUR. The severity scale still owns the chart's bars and the event list's
 * dots, and it is never borrowed elsewhere. Around it the design spends four
 * more hues, each on something that STATES A VALUE or names a finding:
 *   • the state word and the still-open clause — red/amber/green for "is this
 *     still happening", which is a reading, not a hue per question;
 *   • the accent, on the affected window: the band, its callout, the scale
 *     strip's right-hand run and the readout's in-cluster mark all wear it,
 *     so the four places that point at the same hours look like one thing;
 *   • a tone per finding tile — and the reason this is affordable now, when
 *     the previous pass withdrew exactly this device, is that an ABSENCE no
 *     longer gets a tile. The chain it was withdrawn from had four rungs of
 *     which two usually held nothing, and a pastel field whose emptiest boxes
 *     are as loud as its findings scans as findings. Every tinted square here
 *     sits beside something that was found;
 *   • green on the checks, which is the design's and is the one piece of
 *     colour on this card that claims more than it can prove — a tick is the
 *     vocabulary of a test that passed, and not finding a recorded change is
 *     not the same as there having been none. The titles carry that load:
 *     each states what was NOT found, never that anything is fine.
 *
 * CONTAINERS. The previous pass had none — every band sat on the card's own
 * ground, ranked by a hairline and by space — because nine same-radius boxes
 * had flattened the hierarchy until the eye had no entry point. The design
 * brings back four, and they are not the same nine: the chart, the findings,
 * the checks and the feedback row, each holding a band that is already a
 * section, with nothing nested inside another. The tiles are the only boxes
 * within a box, and they are the card's leaves.
 *
 * Responsive: every multi-column band collapses to one under 620px.
 */

.nw-ac {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 18px 20px 16px;
  margin-top: 13px;
}

/* The four bands, all one shape. A panel and not a rule between sections,
   because the chart now carries annotations that run to its own edges (the
   callout, the scale strip) and needs a boundary they can stop against. */
.nw-ac-chart,
.nw-ac-sec,
.nw-ac .nw-acf {
  background: color-mix(in srgb, var(--ink) 3.5%, transparent);
  border: 1px solid var(--line);
  border-radius: 13px;
  padding: 15px 16px 14px;
  margin-top: 14px;
}
/* The chart measures itself, so the strip can decide whether its own labels
   fit from the width of the BAR rather than the width of the window. This
   card is 820px in the Outlook dialog and 380 in the widget at one and the
   same viewport, which is why a media query is the wrong instrument here. */
.nw-ac-chart { container: nw-ac-chart / inline-size; }

/* --- the finding -------------------------------------------------------- */

.nw-ac-top {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}
.nw-ac-topb { flex: 1 1 320px; min-width: 0; }

.nw-ac-meta {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--soft);
  font-variant-numeric: tabular-nums;
  margin-bottom: 9px;
}
.nw-ac-dot { color: var(--faint); }

/* A dot and a word, not a bordered pill. A pill is a control's shape, and
   this is a reading sitting in a line of readings; the border made it the one
   thing at the top of the card that looked pressable. Colour follows the
   state's urgency, not the severity scale: whether the thing is still
   happening is a different question from how bad each alert was. */
.nw-ac-state {
  --tone: var(--soft);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-display);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .04em;
  color: var(--tone);
}
.nw-ac-state::before {
  content: '';
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--tone);
}
.nw-ac-state.s-live { --tone: var(--red, #e45959); }
.nw-ac-state.s-recovering { --tone: var(--amber, #ffa059); }
.nw-ac-state.s-resolved { --tone: var(--green, #46b27e); }
/* The one thing on this card that is still happening gets the only ambient
   motion on it. Both ends of the cycle are visible states. */
.nw-ac-state.s-live::before {
  animation: nw-ac-pulse 2.4s cubic-bezier(.4, 0, .6, 1) infinite;
}
@keyframes nw-ac-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .34; } }

/* The finding, at the size a finding deserves. Numbers carry full ink and
   weight and the words between them recede: emphasis by weight, because hue
   is spoken for. */
.nw-ac-find {
  font-family: var(--font-display);
  font-size: clamp(19px, 2.6vw, 25px);
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: -.015em;
  color: var(--soft);
  margin: 0;
  text-wrap: balance;
}
.nw-ac-find b {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* Only rendered when the host does NOT already print it (showTitle). Inside
   the Outlook dialog the header says it, and the finding takes the heading
   rather than sitting under a second title one line apart. */
.nw-ac-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 4px;
}

/* The dialog's own header is where this card's title lives, and at 14px it
   was a caption over a 25px finding. Scoped by :has so no other Outlook
   dialog is touched; where :has is unsupported the title merely stays small,
   which is the state it shipped in. */
.nw-ol-dialog:has(.nw-ac) .nw-ol-day-title {
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -.02em;
}
.nw-ol-dialog:has(.nw-ac) .nw-ol-dlg-head { margin-bottom: 14px; }

/* The clause that decides whether anyone has to act, directly under the
   finding instead of seven blocks below it. */
.nw-ac-open {
  --tone: var(--soft);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 10px 0 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--soft);
}
.nw-ac-open.t-amber { --tone: var(--amber, #ffa059); }
.nw-ac-open.t-green { --tone: var(--green, #46b27e); }
.nw-ac-open-i { flex: none; display: inline-flex; color: var(--tone); }
.nw-ac-open-n {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  color: var(--tone);
  font-variant-numeric: tabular-nums;
}

/* Whether this volume is normal for the client. A reading of the finding,
   so it sits under it at body size; the lead carries the verdict. */
.nw-ac-vol {
  margin: 8px 0 0;
  max-width: 74ch;
  font-size: 13px;
  line-height: 1.5;
  color: var(--soft);
  font-variant-numeric: tabular-nums;
}
.nw-ac-vol b { font-weight: 600; color: var(--ink); }
.nw-ac-vol.t-unusual b { color: var(--amber, #ffa059); }

.nw-ac-warn {
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--amber, #ffa059);
  margin: 12px 0 0;
  max-width: 74ch;
}
.nw-ac-hedge {
  font-size: 12px;
  line-height: 1.55;
  color: var(--faint);
  margin: 12px 0 0;
  max-width: 74ch;
}

/* The card's primary action, and its only filled control. It used to be a
   chip in the corner of the still-active box, which on a narrow dialog put
   the one thing a reader might click below the fold. */
.nw-ac-gowrap {
  position: relative;
  flex: none;
  align-self: flex-start;
  display: inline-flex;
}
.nw-ac-go {
  flex: none;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: .01em;
  /* Ink from the surface, not white: on the light theme the accent is a mid
     blue and white would be the one AA failure on this card. */
  color: var(--card);
  background: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 10px;
  padding: 9px 15px;
  min-height: 38px;
  cursor: pointer;
  white-space: nowrap;
  transition: background .15s, border-color .15s;
}
.nw-ac-go:hover {
  background: color-mix(in srgb, var(--accent) 86%, var(--ink));
  border-color: color-mix(in srgb, var(--accent) 86%, var(--ink));
}
.nw-ac-go:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

/* What stands where the deep link would be when nothing is still open.
 *
 * Neither a disabled button nor a second verdict. There is nothing behind the
 * control once every alert has recovered — the page it opens lands on the Live
 * tab and would be empty — so this slot accounts for the absent button and
 * claims nothing about the cluster; `activeLine` two lines below already says
 * "All related alerts have recovered", and an outlined green tick here was
 * that sentence twice, forty pixels apart.
 *
 * So: no border, no tick, no accent. Just the note, at the button's own size
 * so the header keeps its shape between states. */
.nw-ac-none {
  flex: none;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 9px 2px;
  font-size: 12.5px;
  color: var(--faint);
  white-space: nowrap;
}
.nw-ac-go svg { transition: transform .18s cubic-bezier(.2, .8, .2, 1); }
.nw-ac-go:hover svg { transform: translateX(2px); }

/* --- hints --------------------------------------------------------------
 *
 * The (i) beside a heading, and the panel that opens from it. Everything
 * behind one is a rule that QUALIFIES a claim already visible in full, so a
 * reader who never opens one is less informed but never misled.
 */
.nw-ac-hint {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  margin-left: 6px;
}
.nw-ac-hint-b {
  display: inline-flex;
  padding: 0;
  margin: 0;
  border: 0;
  background: none;
  line-height: 0;
  color: var(--faint);
  cursor: pointer;
  transition: color .15s;
}
.nw-ac-hint-b:hover { color: var(--accent); }
.nw-ac-hint-b:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 999px;
}
/* Resets the face it inherits: it hangs off headings and small-caps labels,
   and it is a paragraph, not a heading fragment. */
.nw-ac-hint-p {
  position: absolute;
  top: calc(100% + 8px);
  z-index: 8;
  width: max-content;
  max-width: 264px;
  padding: 9px 11px;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0;
  text-transform: none;
  color: var(--soft);
  background: var(--card);
  border: 1px solid var(--line2);
  border-radius: 10px;
  box-shadow: 0 8px 22px -10px rgba(0, 0, 0, .6);
  animation: nw-ac-tip-in .12s ease-out both;
}
.nw-ac-hint-p.a-start { left: -10px; }
.nw-ac-hint-p.a-end { right: -10px; }
/* Hung off the button itself rather than an (i) beside it: the whole control
   is the thing being explained, and a glyph next to a filled button reads as
   a second control. */
.nw-ac-hint-p.is-go { top: calc(100% + 9px); max-width: 290px; }

/* --- the chart ---------------------------------------------------------- */

.nw-ac-chart-h {
  display: flex;
  align-items: flex-start;
  gap: 8px 14px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.nw-ac-chart-hb { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.nw-ac-chart-t,
.nw-ac-label {
  display: flex;
  align-items: center;
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -.005em;
  color: var(--ink);
  margin: 0;
}
/* Neither uppercase nor tracked: it is a caveat about what is counted, and
   setting it as a label would make a footnote shout. */
.nw-ac-chart-w { font-size: 11.5px; color: var(--faint); }
.nw-ac-legend {
  margin-left: auto;
  display: flex;
  gap: 13px;
  flex-wrap: wrap;
  padding-top: 2px;
}
.nw-ac-legend-i {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: var(--soft);
}
.nw-ac-legend-i i { flex: none; width: 9px; height: 9px; border-radius: 2px; }

/* The gutter the y labels hang in. On the wrapper rather than the plot, so
   the callout row, the axis and the scale strip all start at the plot's own
   left edge and every annotation shares its column arithmetic. */
.nw-ac-body { padding-left: 30px; margin-top: 14px; }

/* Four grids on ONE column template, so every annotation lands on exact
   column edges with no percentages to drift. The scale strip alone drops the
   gap — it is a continuous bar, and a divider floating in a 3px gutter reads
   as a break in the bar rather than a boundary within it. */
.nw-ac-callrow,
.nw-ac-plot,
.nw-ac-axis,
.nw-ac-scale-cap {
  display: grid;
  grid-template-columns: repeat(var(--nw-ac-n, 24), minmax(0, 1fr));
  gap: 3px;
}
.nw-ac-scale {
  display: grid;
  grid-template-columns: repeat(var(--nw-ac-n, 24), minmax(0, 1fr));
}

.nw-ac-plot {
  position: relative;
  height: 168px;
  border-bottom: 1px solid var(--line2);
}
.nw-ac-plot > * { grid-row: 1; }
.nw-ac-col {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  min-width: 0;
  /* Above the band and the gridlines: the data is never behind its own
     annotation. */
  position: relative;
  z-index: 2;
}
/* An hour's usual range, behind its bar. Neutral: severity owns the warm
   colours and the band owns the accent. A collapsed range (p25 = p75) still
   draws a line, so "usually exactly this" does not vanish. */
.nw-ac-usual {
  position: relative;
  z-index: 1;
  height: 100%;
  min-width: 0;
}
.nw-ac-usual i {
  position: absolute;
  left: -1px;
  right: -1px;
  min-height: 2px;
  border-radius: 1px;
  background: color-mix(in srgb, var(--ink) 11%, transparent);
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--ink) 22%, transparent);
}
.nw-ac-legend-i.is-usual i {
  background: color-mix(in srgb, var(--ink) 16%, transparent);
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--ink) 30%, transparent);
}

.nw-ac-stack {
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
}
.nw-ac-stack i { display: block; width: 100%; min-height: 2px; }
.nw-ac-stack i:first-child { border-radius: 0 0 1px 1px; }
.nw-ac-stack i:last-child { border-radius: 2px 2px 0 0; }
/* An hour with nothing in it keeps a tick on the baseline. Drawn as a gap, a
   quiet night read as missing data rather than as a measured zero — and this
   chart's job is to say how unusual the banded hours were, which needs the
   quiet ones to be legible as quiet. */
.nw-ac-col.is-quiet .nw-ac-stack::after {
  content: '';
  display: block;
  width: 100%;
  height: 2px;
  border-radius: 1px;
  background: var(--track);
}

/* The y scale. Bars used to be drawn against their own tallest member, which
   made every chart look equally busy — a night peaking at three and a storm
   peaking at ninety drew the same silhouette. The lines and the bars measure
   from one baseline by construction: both are percentages of this element,
   which is the plot. */
.nw-ac-ticks {
  grid-column: 1 / -1;
  position: relative;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}
.nw-ac-tickline {
  position: absolute;
  left: 0;
  right: 0;
  height: 0;
  border-top: 1px solid var(--line);
}
/* Zero already has the plot's own baseline rule; a second line on it reads
   as a doubled stroke. The label stays. */
.nw-ac-tickline:first-child { border-top-color: transparent; }
.nw-ac-tickline i {
  position: absolute;
  right: calc(100% + 9px);
  top: 50%;
  transform: translateY(-50%);
  font-style: normal;
  font-size: 9.5px;
  color: var(--faint);
  font-variant-numeric: tabular-nums;
}

/* --- the hover readout --------------------------------------------------
 *
 * This was the browser's `title` attribute: a tooltip in the OS font on the
 * OS ground, a second late, unstyleable, absent on touch and unreachable
 * from the keyboard. It is now the card's own object, in the card's own
 * tokens, and it holds the per-severity split the stacked bar encodes but
 * which cannot be read off it precisely.
 */

/* The column under the pointer, so the readout and the bar it describes read
   as one thing. A wash, not an outline: an outline on a column this narrow
   reads as a fifth severity. */
.nw-ac-col.is-cursor { background: color-mix(in srgb, var(--ink) 7%, transparent); }
.nw-ac-col:focus { outline: none; }
.nw-ac-col:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -1px;
  border-radius: 3px;
}

/* The wrapper occupies the hovered COLUMN of the plot grid — the same trick
   the band uses — so the box needs no measuring and no percentage arithmetic
   to sit over the right hour. */
.nw-ac-tip {
  position: relative;
  height: 100%;
  z-index: 6;
  /* The box must never sit between the pointer and the column it describes,
     which would flicker the readout off the instant it appeared. */
  pointer-events: none;
}
.nw-ac-tip-b {
  position: absolute;
  top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 132px;
  padding: 8px 10px;
  background: var(--card);
  border: 1px solid var(--line2);
  border-radius: 10px;
  /* An offset and a soft blur. This is the one element on the plot that is
     genuinely floating above the surface, so it is the one place a shadow
     states something true. */
  box-shadow: 0 6px 18px -8px rgba(0, 0, 0, .55);
  animation: nw-ac-tip-in .12s ease-out both;
}
@keyframes nw-ac-tip-in {
  from { opacity: 0; transform: translateY(-3px); }
  to { opacity: 1; transform: none; }
}
/* The readout is wider than the column it points at, so near either edge it
   leans back into the chart rather than hanging off it — `edgeAnchor`, the
   same rule the band's callout hangs from. The centred case needs its own
   keyframes: the entrance animates `transform`, which is also what centres
   it, so one shorthand has to carry both. */
.nw-ac-tip.a-mid .nw-ac-tip-b {
  left: 50%;
  transform: translateX(-50%);
  animation-name: nw-ac-tip-in-mid;
}
@keyframes nw-ac-tip-in-mid {
  from { opacity: 0; transform: translate(-50%, -3px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}
.nw-ac-tip.a-start .nw-ac-tip-b { left: -2px; }
.nw-ac-tip.a-end .nw-ac-tip-b { right: -2px; }

.nw-ac-tip-w {
  font-family: var(--font-display);
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.nw-ac-tip-n { font-size: 11px; color: var(--soft); font-variant-numeric: tabular-nums; }

.nw-ac-tip-rows {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--line);
}
/* Swatch, name, count — the count in its own column so three severities read
   down the right edge instead of ragged. */
.nw-ac-tip-r {
  display: grid;
  grid-template-columns: 9px 1fr auto;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  color: var(--soft);
  white-space: nowrap;
}
/* The same 9px square the legend uses: a swatch is a sample of the mark. */
.nw-ac-tip-r i { width: 9px; height: 9px; border-radius: 2px; }
.nw-ac-tip-r .v {
  font-family: var(--font-display);
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.nw-ac-tip-u {
  margin-top: 4px;
  font-size: 11px;
  color: var(--faint);
  font-variant-numeric: tabular-nums;
}
/* Says this hour is inside the banded region, in the accent the band wears. */
.nw-ac-tip-in {
  margin-top: 5px;
  font-family: var(--font-display);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--accent);
}

/* --- the affected window ------------------------------------------------
 *
 * ONE region across the cluster's columns, behind the bars, with a callout
 * above it and a run of the scale strip beneath it. The previous treatment
 * tinted each banded column, which read as a PHANTOM BAR whenever a banded
 * hour was quiet — a block the height of the chart with no data in it. A
 * single region cannot be misread that way: it spans several columns at
 * once, it is plainly behind the bars rather than among them, and it now
 * carries a label saying what it is, when it was and what landed in it.
 *
 * Accent, not red. The bars already spend the whole warm range on severity;
 * a red region over red bars would be mud, and would state a second opinion
 * about badness that this band does not hold.
 */
.nw-ac-band {
  position: relative;
  z-index: 1;
  height: 100%;
  /* Half a gap either side, so the region reads as covering whole hours
     rather than stopping short of the bars at its edges. */
  margin: 0 -2px;
  border-left: 1px solid color-mix(in srgb, var(--accent) 62%, transparent);
  border-right: 1px solid color-mix(in srgb, var(--accent) 62%, transparent);
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--accent) 7%, transparent),
    color-mix(in srgb, var(--accent) 22%, transparent));
}
/* The rule along its base is the line that says "these hours". It is also the
   card's one authored moment: it sweeps across the band on open, from a band
   already at rest, so nothing appears out of nowhere. */
.nw-ac-band::after {
  content: '';
  position: absolute;
  left: -1px;
  right: -1px;
  bottom: 0;
  height: 2px;
  background: var(--accent);
  transform-origin: left;
  animation: nw-ac-sweep .62s cubic-bezier(.16, 1, .3, 1) both;
}
@keyframes nw-ac-sweep { from { transform: scaleX(0); } to { transform: scaleX(1); } }

/* The callout gets its own row above the plot rather than the plot's
   headroom: three lines do not fit in the space the bars need, and a caption
   that overlaps the tallest bar of the day is a caption over the one hour
   the reader came to look at. */
.nw-ac-callrow { height: 76px; margin-bottom: 5px; }
.nw-ac-call {
  --call-line: color-mix(in srgb, var(--accent) 48%, transparent);
  --call-bg: color-mix(in srgb, var(--accent) 13%, var(--card));
  position: relative;
}
.nw-ac-call-b {
  position: absolute;
  bottom: 7px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: max-content;
  max-width: 236px;
  padding: 8px 11px;
  background: var(--call-bg);
  border: 1px solid var(--call-line);
  border-radius: 10px;
  box-shadow: 0 8px 20px -12px rgba(0, 0, 0, .6);
}
/* The wedge points at the band, from the box. Rotated square rather than a
   border triangle so it can carry the box's own fill AND its hairline. */
.nw-ac-call-b::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 50%;
  margin-left: -4.5px;
  width: 9px;
  height: 9px;
  transform: rotate(45deg);
  background: var(--call-bg);
  border-right: 1px solid var(--call-line);
  border-bottom: 1px solid var(--call-line);
}
/* The callout is wider than a narrow band. Centred on a one-hour cluster at
   either end of the day it would hang off the chart, so a band near an edge
   pins its callout to that edge instead (see bandSpan). */
.nw-ac-call.a-mid .nw-ac-call-b { left: 50%; transform: translateX(-50%); }
.nw-ac-call.a-start .nw-ac-call-b { left: -1px; }
.nw-ac-call.a-end .nw-ac-call-b { right: -1px; }
.nw-ac-call-b .l {
  font-family: var(--font-display);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--accent);
}
.nw-ac-call-b .w {
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.nw-ac-call-b .s {
  font-size: 11px;
  color: var(--soft);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.nw-ac-axis { margin-top: 7px; }
.nw-ac-axis span {
  font-size: 9.5px;
  color: var(--faint);
  text-align: center;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* The scale strip: a RULER for the chart above it.
 *
 * The bar is the whole day and the slice marks the affected window at its own
 * columns, placed on the plot's column template like every other annotation
 * on this chart — so where the slice sits IS where the cluster happened.
 *
 * It shipped as two segments split at the window's left edge, the right one
 * carrying the window's duration. That only read correctly when the cluster
 * sat against the right edge of the day, which is where the design's example
 * sat: anywhere else it labelled hours of context with the cluster's own
 * thirteen minutes, and centred the caption on the wrong place.
 */
.nw-ac-scale {
  margin-top: 11px;
  min-height: 32px;
  align-items: center;
  background: var(--chip-bg);
  border: 1px solid var(--line);
  border-radius: 9px;
  /* The window's rules run the full height of the bar, so the bar has to clip
     them back to its own corners. */
  overflow: hidden;
}
/* The label measures the CONTEXT run, centred in it, and takes one arrow: the
   one on the outer end of the run, which is the bar's own end. The run's
   other end is the window, and the window brings its own mark there.

   The padding is what keeps the words clear of BOTH marks — its own and the
   window's, which hangs outside the slice and over this run. */
.nw-ac-scale-ctx {
  grid-row: 1;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 26px;
  min-width: 0;
  font-family: var(--font-display);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: .01em;
  color: var(--faint);
}
/* Out of the flow, so a centred label stays centred on its run rather than on
   what is left of it after an arrow. */
.nw-ac-scale-ctx .a {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  pointer-events: none;
}
.nw-ac-scale-ctx .a.l { left: 9px; }
.nw-ac-scale-ctx .a.r { right: 9px; }
.nw-ac-scale-ctx.o-left .a.r,
.nw-ac-scale-ctx.o-right .a.l { display: none; }
/* Ellipsised rather than dropped: a run four columns wide holds the arrows
   and enough of the phrase to be recognised, and the caption below names the
   thing that actually matters. */
.nw-ac-scale-ctx em {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: normal;
}
/* The window, at its own columns: a tinted segment of the bar between two
   full-height rules, which is what turns the strip into a ruler with a
   boundary rather than a bar with a lozenge floating on it. The rules are the
   load-bearing part — at one column wide, the common case since most storms
   start and finish inside an hour, they are most of what there is to see, so
   they carry the accent at full strength and the fill stays a wash. */
.nw-ac-scale-win {
  grid-row: 1;
  position: relative;
  z-index: 1;
  align-self: stretch;
  margin: 0 -1px;
  min-width: 9px;
  box-sizing: border-box;
  background: color-mix(in srgb, var(--accent) 17%, transparent);
  border-left: 1.5px solid var(--accent);
  border-right: 1.5px solid var(--accent);
}
/* The pair of arrows the design brackets the window with, one from each side,
   both pointing in at it. Hung off the rules rather than laid out beside them
   so they take no width from the bar: at one column of twenty-four there is
   none to take, and a mark that disappears at the width the card is usually
   read at is not a mark. */
.nw-ac-win-a {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  color: var(--accent);
  pointer-events: none;
}
.nw-ac-win-a.l { right: 100%; margin-right: 5px; }
.nw-ac-win-a.r { left: 100%; margin-left: 5px; }
/* The duration, in the free run beside the window (markerRun) and just past
   the arrow on that side — which is where the design has it, between the two
   arrows. The padding is the arrow's room, and is the reason the words are
   the part that goes when the bar runs out of width and the marks are not. */
.nw-ac-scale-mark {
  grid-row: 1;
  display: inline-flex;
  align-items: center;
  padding: 7px 8px;
  min-width: 0;
  font-family: var(--font-display);
  font-size: 11px;
  font-weight: 600;
  color: var(--accent);
  white-space: nowrap;
}
.nw-ac-scale-mark.m-right { padding-left: 22px; }
.nw-ac-scale-mark.m-left { padding-right: 22px; }
.nw-ac-scale-mark b { font-weight: 600; font-variant-numeric: tabular-nums; }

.nw-ac-scale-cap { margin-top: 6px; }
/* Sentence case at reading size, not a letterspaced micro-caption: the
   duration has moved up into the bar and what is left is a name for the
   segment above it. */
.nw-ac-scale-cap span {
  grid-row: 1;
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  justify-content: center;
  font-family: var(--font-display);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: .01em;
  color: var(--accent);
  white-space: nowrap;
}
.nw-ac-scale-cap b { font-weight: 600; font-variant-numeric: tabular-nums; }
/* The copy of the duration that loses. By default the strip's own wins and
   this one is hidden; below a bar that can hold it they swap. The 620px rule
   further down is the same swap for browsers that do not read @container. */
.nw-ac-scale-cap .dup { display: none; }
@container nw-ac-chart (max-width: 760px) {
  .nw-ac-scale-mark { display: none; }
  .nw-ac-scale-cap .dup { display: inline; }
}
.nw-ac-scale-cap i { font-style: normal; opacity: .6; }
/* The caption is far wider than a one-hour slice, so near either end of the
   day it leans back into the chart instead of hanging off it — the same
   `edgeAnchor` rule the callout and the hover readout hang from. */
.nw-ac-scale-cap span.a-start { justify-self: start; }
.nw-ac-scale-cap span.a-end { justify-self: end; }

/* --- the findings ------------------------------------------------------- */

.nw-ac-sub {
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--faint);
  margin: 3px 0 13px;
  font-variant-numeric: tabular-nums;
}

/* Two to a row on the dialog, one on a phone. Every tile here is an ANSWER —
   a question that came back empty is a row in the checks band below, which is
   what makes the tones affordable at all. */
.nw-ac-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(258px, 1fr));
  gap: 12px;
}
.nw-ac-tile {
  --tone: var(--soft);
  display: flex;
  align-items: flex-start;
  gap: 13px;
  min-width: 0;
  padding: 14px 15px;
  border: 1px solid color-mix(in srgb, var(--tone) 26%, var(--line));
  border-radius: 12px;
  background: color-mix(in srgb, var(--tone) 6%, transparent);
}
.nw-ac-tile.t-blue { --tone: var(--accent); }
/* NOT a severity. The tile states which trigger fired most often, never how
   bad it was; it is the row's only warm tile so the row has one focal point
   rather than five. */
.nw-ac-tile.t-red { --tone: var(--red, #e45959); }
.nw-ac-tile.t-violet { --tone: var(--chart-third, #9085e9); }
.nw-ac-tile.t-amber { --tone: var(--amber, #ffa059); }
.nw-ac-tile.t-slate { --tone: var(--soft); }
.nw-ac-tile-i {
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  color: var(--tone);
  background: color-mix(in srgb, var(--tone) 16%, transparent);
  border: 1px solid color-mix(in srgb, var(--tone) 26%, transparent);
}
/* A stylesheet beats a presentational width/height attribute, which is how
   one 18px icon set serves a 21px square without a second component. */
.nw-ac-tile-i svg { width: 21px; height: 21px; }
.nw-ac-tile-b { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.nw-ac-tile-l {
  font-family: var(--font-display);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .01em;
  color: var(--tone);
}
.nw-ac-tile-v {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--ink);
  /* Trigger names are full sentences by design (nothing paraphrases them) and
     a host group can be one long token, so the tile breaks rather than
     forcing the row wider than the card. */
  overflow-wrap: anywhere;
}
/* An identifier the client can paste into Zabbix, not prose. */
.nw-ac-tile-v.is-mono {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 500;
}
/* The arithmetic under the answer. Counts at full ink and weight, the words
   between them receding — the same rule the headline follows, for the same
   reason: the numbers are what a reader is glancing for. */
.nw-ac-tile-st {
  margin-top: 2px;
  font-size: 13px;
  color: var(--soft);
  font-variant-numeric: tabular-nums;
}
.nw-ac-tile-st b {
  font-family: var(--font-display);
  font-weight: 700;
  color: var(--ink);
}
.nw-ac-tile-s { font-size: 11.5px; color: var(--soft); font-variant-numeric: tabular-nums; }
.nw-ac-tile-n {
  margin-top: 5px;
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--faint);
}

/* --- seen before, at any count ------------------------------------------ */

/* Open, the history tile takes the whole row, so eighteen dates flow into
   columns instead of stretching the tile beside it. */
.nw-ac-tile.is-open { grid-column: 1 / -1; }

/* One cell per day, oldest first. Capped in width: at the open tile's full
   row a 30-cell strip stretched to 780px reads as a progress bar. */
.nw-ac-days {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-width: 400px;
  margin: 9px 0 3px;
}
.nw-ac-days-bar {
  display: grid;
  grid-template-columns: repeat(var(--nw-ac-days), minmax(0, 1fr));
  align-items: end;
  gap: 2px;
  height: 18px;
}
.nw-ac-days-bar i {
  height: 100%;
  border-radius: 2px;
  background: color-mix(in srgb, var(--ink) 42%, transparent);
  transition: background-color .12s ease;
}
/* Twice or more that day: the same cell, at full ink. A count, not a
   severity — the readout on hover gives the number. */
.nw-ac-days-bar i.m { background: color-mix(in srgb, var(--ink) 82%, transparent); }
/* A day it did not happen keeps a tick on the baseline, as a quiet hour
   does on the chart: a gap would read as a day nobody looked at. */
.nw-ac-days-bar i.q {
  height: 3px;
  background: color-mix(in srgb, var(--ink) 16%, transparent);
}
.nw-ac-days-bar i.is-cur { background: var(--accent); }
.nw-ac-days-bar i.q.is-cur { height: 6px; }
.nw-ac-days-ends {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  font-size: 10.5px;
  color: var(--faint);
  font-variant-numeric: tabular-nums;
}
.nw-ac-days-ends b {
  text-align: center;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
}

.nw-ac-hist {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
  font-size: 11.5px;
  color: var(--soft);
  font-variant-numeric: tabular-nums;
}
.nw-ac-hist.is-open {
  display: block;
  columns: 13.5em;
  column-gap: 28px;
}
.nw-ac-hist.is-open li { break-inside: avoid; padding-bottom: 3px; }

.nw-ac-hist-more {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin: 5px 0 0 -4px;
  padding: 3px 4px;
  border: 0;
  border-radius: 6px;
  background: none;
  font: 600 12px/1.2 var(--font-display);
  color: var(--accent);
  cursor: pointer;
  transition: background-color .12s ease;
}
.nw-ac-hist-more:hover { background: color-mix(in srgb, var(--accent) 10%, transparent); }
.nw-ac-hist-more:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
.nw-ac-hist-more svg {
  width: 13px;
  height: 13px;
  transform: rotate(90deg);
  transition: transform .2s ease;
}
.nw-ac-hist-more[aria-expanded='true'] svg { transform: rotate(-90deg); }
@media (prefers-reduced-motion: reduce) {
  .nw-ac-days-bar i,
  .nw-ac-hist-more,
  .nw-ac-hist-more svg { transition: none; }
}

/* --- what was looked for and not found ---------------------------------- */

/* Each check states its RULE, which is the part that had to survive: "no
   recent configuration change" alone reads as a verdict on the environment,
   and naming the 45-minute window hands the reader what they need to judge
   it. Where the rule is too long for the row it sits behind the (i) instead.
   The tick is the design's; see the colour note at the top of this file for
   what it costs and what pays for it. */
.nw-ac-checks {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 22px;
}
.nw-ac-check { display: flex; align-items: flex-start; gap: 11px; min-width: 0; }
/* The divider belongs to the right-hand COLUMN, not to "every check after the
   first": with a third check (the volume verdict's) the grid wraps, and a
   sibling rule hung a column divider on the left edge of the new row. Rows
   after the first take a rule above instead. */
.nw-ac-check:nth-child(even) { border-left: 1px solid var(--line); padding-left: 22px; }
.nw-ac-check:nth-child(n + 3) { border-top: 1px solid var(--line); padding-top: 11px; }
.nw-ac-check-i {
  flex: none;
  margin-top: 1px;
  display: inline-flex;
  color: var(--green, #46b27e);
}
.nw-ac-check-b { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.nw-ac-check-t {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
}
.nw-ac-check-n { font-size: 11.5px; line-height: 1.45; color: var(--faint); }

/* --- the event list ----------------------------------------------------- */

/* Rules, not a box: the list is the widget's fallback for the incidents page
   and belongs to the card's own column, like everything else outside the
   four bands. */
.nw-ac-members {
  border-top: 1px solid var(--line);
  margin-top: 16px;
}
.nw-ac-member {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) auto auto auto;
  gap: 12px;
  align-items: center;
  padding: 9px 0;
  font-size: 12.5px;
  border-bottom: 1px solid var(--line);
}
.nw-ac-member .s { width: 8px; height: 8px; border-radius: 999px; }
.nw-ac-member .n { color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nw-ac-member .h { color: var(--soft); font-family: var(--font-mono); font-size: 11px; }
.nw-ac-member .t { color: var(--faint); font-variant-numeric: tabular-nums; }
.nw-ac-member .r { color: var(--faint); font-size: 11px; }
.nw-ac-member .r.open { color: var(--amber, #ffa059); }
.nw-ac-member-more {
  padding: 8px 0;
  font-size: 11.5px;
  color: var(--faint);
}

@media (max-width: 620px) {
  .nw-ac { padding: 15px 14px 14px; }
  .nw-ac-chart, .nw-ac-sec, .nw-ac .nw-acf { padding: 13px 12px 12px; }
  .nw-ac-go, .nw-ac-none, .nw-ac-gowrap { width: 100%; }
  .nw-ac-go, .nw-ac-none { justify-content: center; box-sizing: border-box; }
  .nw-ac-body { padding-left: 24px; }
  .nw-ac-callrow, .nw-ac-plot, .nw-ac-axis, .nw-ac-scale-cap { gap: 2px; }
  /* Twenty-four columns of a phone-width bar is about ten pixels each, and
     the strip's label wants sixty. It moves back down to the caption. */
  .nw-ac-scale-mark { display: none; }
  .nw-ac-scale-cap .dup { display: inline; }
  .nw-ac-legend { margin-left: 0; }
  .nw-ac-checks { grid-template-columns: minmax(0, 1fr); }
  /* One column: the divider turns from a rule between columns into a rule
     between stacked rows. */
  .nw-ac-check + .nw-ac-check {
    border-left: 0;
    padding-left: 0;
    padding-top: 11px;
    border-top: 1px solid var(--line);
  }
  /* Five columns of event detail do not fit a phone; the recovery note and
     the host drop to their own lines rather than being truncated away. */
  .nw-ac-member { grid-template-columns: 8px minmax(0, 1fr); gap: 4px 12px; }
  .nw-ac-member .h, .nw-ac-member .t, .nw-ac-member .r { grid-column: 2; }
  .nw-ac-member .n { white-space: normal; }
}

@media (prefers-reduced-motion: reduce) {
  .nw-ac-state.s-live::before,
  .nw-ac-band::after { animation: none; }
  /* The readout still has to arrive centred, so the centred case cannot just
     drop its animation — it keeps the transform and loses the movement. */
  .nw-ac-tip-b, .nw-ac-hint-p { animation: none; }
  .nw-ac-tip.a-mid .nw-ac-tip-b { transform: translateX(-50%); }
  .nw-ac-go,
  .nw-ac-go svg,
  .nw-ac-hint-b { transition: none; }
}

/* --- the Outlook row ----------------------------------------------------
 *
 * The page's entry point to the card, in the 2026-09-14 design. Still one
 * thing tall by the 2026-09-09 ruling for this slot — the full card is what
 * opens on click — but it is now a row of readings rather than a heading over
 * a sentence, because this slot is scanned and not read.
 *
 * ONE TONE per row, set here from the chip's state and worn by the chip and
 * the sparkline's highlight together. The row states its urgency once.
 */
.nw-act {
  --tone: var(--green, #46b27e);
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  text-align: left;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 15px 17px;
  margin-top: 13px;
  cursor: pointer;
  color: inherit;
  transition: border-color .15s, background .15s, box-shadow .15s;
}
.nw-act.t-amber { --tone: var(--amber, #ffa059); }
.nw-act.t-green { --tone: var(--green, #46b27e); }
/* One authored moment, on the only interaction this row has: the accent
   hairline arrives and the surface lifts. Both sides of the transition are
   already-visible states, so nothing appears out of nowhere. */
.nw-act:hover {
  background: var(--hover);
  border-color: color-mix(in srgb, var(--accent) 40%, var(--line));
  box-shadow: 0 2px 12px -6px color-mix(in srgb, var(--accent) 55%, transparent);
}
.nw-act:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

/* The slot's mark. Accent, never the row's tone: it names WHO is speaking,
   which does not change with how bad the storm is. */
.nw-act-gl {
  width: 48px;
  height: 48px;
  flex: none;
  border-radius: 13px;
  display: grid;
  place-items: center;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 11%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent) 24%, transparent);
}

.nw-act-b { display: flex; flex-direction: column; gap: 4px; min-width: 0; flex: 1 1 auto; }

/* The eyebrow, carrying the chip beside it — a flex-wrap row, so a long
   label and a chip never collide. */
.nw-act-l {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-family: var(--font-display);
  /* The page's own eyebrow scale, shared with the posture line that sits in
     this slot when there is no cluster (`.nw-ol .insight .il`). The row had
     drifted a step above it, which made two rows of the same rank read as
     two ranks. */
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--accent);
}

/* A pill here, unlike the detail card's bare dot-and-word. This one is a
   COUNT of things still open rather than a reading in a line of readings, and
   at eyebrow size a bare number would be lost against the label beside it. */
.nw-act-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: .1em;
  color: var(--tone);
  background: color-mix(in srgb, var(--tone) 13%, transparent);
  border: 1px solid color-mix(in srgb, var(--tone) 42%, transparent);
  border-radius: 999px;
  padding: 3px 11px 3px 9px;
}
.nw-act-chip::before {
  content: '';
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--tone);
}
/* Live, the chip names the state rather than a count, in the accent: it is
   a process, and the run on the sparkline beside it states the condition. */
.nw-act-chip.c-accent { --tone: var(--accent); }
/* The one thing on this page that is still happening gets the only ambient
   motion on it. Both ends of the cycle are visible states. */
.nw-act.is-live .nw-act-chip::before {
  animation: nw-ac-pulse 2.4s cubic-bezier(.4, 0, .6, 1) infinite;
}

/* The three counts. Full ink and weight on the numbers, the units between
   them receding — the same rule the card's finding follows, and built from
   the same parts, so a host name is never mistaken for a count.
 *
 * 15px, which is the size the row's title shipped at before the redesign and
 * the size the rest of this page sets a row's subject in. The redesign took
 * it to a fluid 17-21px on the reasoning that the counts ARE the row now —
 * but a row in a column of rows does not get to be the page's largest type
 * just because its content got better, and at 21px it was competing with the
 * Outlook headings above it. The weight split is what makes the counts
 * findable here; the size never was. */
.nw-act-h {
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.35;
  letter-spacing: -.005em;
  color: var(--soft);
  overflow-wrap: anywhere;
}
.nw-act-h b {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* When, and how much of it is over. A rule between them: they answer
   different questions, and a reader after one should not read past the other. */
.nw-act-s {
  display: flex;
  align-items: center;
  gap: 11px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: var(--soft);
  font-variant-numeric: tabular-nums;
}
.nw-act-s .sep {
  flex: none;
  width: 1px;
  height: 12px;
  background: var(--line2);
}
.nw-act-s .p { color: var(--faint); }
/* The volume verdict, on its own line: a reading of the day, not of the
   window's clock. A dot, not an icon, so it cannot read as a control. */
.nw-act-v { gap: 7px; color: var(--faint); }
.nw-act-v .m {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--faint);
}
.nw-act-v.v-unusual { color: var(--soft); }
.nw-act-v.v-unusual .m { background: var(--amber, #ffa059); }

/* Live only: the start and the clause that says it is provisional, joined
   by a middot rather than the rule — they are one statement, not two. */
.nw-act-s .dot { color: var(--faint); margin: 0 -7px; }

/* The day's shape, with the cluster's own hours picked out.
 *
 * The only thing on the Outlook page that says whether this storm was
 * unusual FOR THIS CLIENT, which is the question the card behind it exists to
 * answer — and the reason the chevron gave up its word to make room.
 *
 * Two rows on the plot's own column template: the caption and its bracket
 * above, the bars below. Same explicit column placement as the card's chart,
 * for the same reason.
 */
/* The wrapper carries the width, and is what the card over the sparkline
   hangs from. */
.nw-act-sparkw {
  position: relative;
  display: block;
  flex: none;
  width: clamp(170px, 27%, 270px);
}
.nw-act-spark {
  display: grid;
  grid-template-columns: repeat(var(--nw-act-n, 24), minmax(0, 1fr));
  grid-template-rows: 20px 36px;
  /* The column gap is SPARK_GAP in AlertCluster.tsx, which maps the pointer
     to an hour. Change both or neither. */
  gap: 0 2px;
  align-items: end;
  width: 100%;
}
.nw-act-bar {
  grid-row: 2;
  align-self: end;
  min-height: 2px;
  border-radius: 1.5px;
  background: var(--line2);
  transition: opacity .12s ease-out, background-color .12s ease-out;
}
.nw-act-bar.is-in { background: var(--tone); }
/* While the card reads an hour, that hour is the only bar at full strength.
   The rest recede rather than vanish, so the picked-out run still reads as
   picked out. */
.nw-act-spark.is-reading .nw-act-bar { opacity: .42; }
.nw-act-spark.is-reading .nw-act-bar.is-cur { opacity: 1; }
.nw-act-spark.is-reading .nw-act-bar.is-cur:not(.is-in) { background: var(--soft); }
/* The highlighted run, behind its bars. Half a gap either side so it reads as
   covering whole hours rather than stopping short of them. */
.nw-act-spark-w {
  grid-row: 2;
  height: 100%;
  margin: 0 -3px;
  border-radius: 6px;
  /* Two layers, because one tint cannot do this in both themes: a wash that
     reads on the dark card is invisible on the light one, and a wash strong
     enough for the light card swamps the bars on the dark. The tone states
     which run it is; the ink separates the run from its ground. */
  background:
    linear-gradient(color-mix(in srgb, var(--tone) 15%, transparent),
                    color-mix(in srgb, var(--tone) 15%, transparent)),
    color-mix(in srgb, var(--ink) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--tone) 22%, transparent);
  border-bottom: 0;
  box-sizing: border-box;
}
.nw-act-spark-l {
  grid-row: 1;
  position: relative;
  height: 100%;
}
/* The bracket that ties the caption to the run. Drawn from the ITEM, which
   spans the run's columns, so it is the run's width however narrow that is. */
.nw-act-spark-l::after {
  content: '';
  position: absolute;
  /* Centred on the run and at least this wide: most storms start and finish
     inside ONE hour, and a bracket 8px across reads as a stray hook rather
     than as a span. */
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% + 6px);
  min-width: 24px;
  bottom: 0;
  height: 5px;
  border: 1px solid color-mix(in srgb, var(--tone) 55%, transparent);
  border-bottom: 0;
  border-radius: 4px 4px 0 0;
}
/* The bracket's nub: a brace rather than a lid, so it points up at its
   caption instead of merely covering the run. */
.nw-act-spark-l::before {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 5px;
  width: 1px;
  height: 3px;
  background: color-mix(in srgb, var(--tone) 55%, transparent);
}
/* The caption is far wider than a one-hour run, which is the common case, so
   it is centred on the run and leans back into the chart at either edge —
   the same `edgeAnchor` rule the card's band and readout hang from. */
.nw-act-spark-l i {
  position: absolute;
  bottom: 9px;
  left: 50%;
  transform: translateX(-50%);
  font-style: normal;
  font-size: 11px;
  letter-spacing: .01em;
  color: var(--soft);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.nw-act-spark-l.a-start i { left: -2px; transform: none; }
.nw-act-spark-l.a-end i { left: auto; right: -2px; transform: none; }

/* --- the card over the sparkline ----------------------------------------
 *
 * What the sparkline is, in words, for a reader who has never seen one: a
 * title, the hour under the pointer, one sentence on how to read the bars,
 * a key that names the picked-out run and why it wears its colour, and what
 * the heights are measured against.
 *
 * It genuinely floats, so it takes the page's floating-surface treatment —
 * the card ground, the strong hairline, and the shadow DESIGN.md names for a
 * menu, the nearest of its four floating surfaces. It is right-aligned past the
 * sparkline's own edge, into the rule-and-chevron gutter, so the notch has
 * room to reach the last hour without landing on the card's rounded corner.
 */
.nw-act-card {
  position: absolute;
  top: calc(100% + 10px);
  right: -24px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 300px;
  padding: 13px 14px 12px;
  font-family: var(--font-body, inherit);
  font-size: 12.5px;
  line-height: 1.45;
  text-align: left;
  white-space: normal;
  color: var(--soft);
  background: var(--card);
  border: 1px solid var(--line2);
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, .45);
  animation: nw-act-card-in .14s cubic-bezier(.2, .8, .2, 1) both;
}
.nw-act-card.is-above {
  top: auto;
  bottom: calc(100% + 10px);
  animation-name: nw-act-card-in-up;
}
/* A bridge over the gap, so a pointer travelling from the bars to the card
   is never outside both. */
.nw-act-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -11px;
  height: 11px;
}
.nw-act-card.is-above::before { top: auto; bottom: -11px; }
@keyframes nw-act-card-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}
@keyframes nw-act-card-in-up {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

.nw-act-card-p, .nw-act-card-f { text-wrap: pretty; }
.nw-act-card-t {
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--ink);
}

/* The readout: the one part of the card that changes while it is read, so
   it sits in a well of its own — sunk toward the ground, with no border of
   its own, so it reads as a recess in the card rather than a box on it. Two
   fixed lines, so scrubbing from a quiet hour to a busy one never changes the
   card's height. */
.nw-act-card-r {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: -2px;
  padding: 8px 10px 9px;
  border-radius: 8px;
  background: var(--recess, var(--inset, transparent));
}
.nw-act-card-r .w {
  font-family: var(--font-display);
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.nw-act-card-r .n {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 18px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
/* The hour is in the window: dot, word and the run's own tone, the same
   three the key below uses for the same claim. */
.nw-act-card-r em {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-style: normal;
  font-family: var(--font-display);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--tone);
}
.nw-act-card-r em::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--tone);
}

/* The key. Each swatch is a sample of the mark — a bar, at the bars' own
   radius — not a generic square. */
.nw-act-card-k {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.nw-act-card-k .kr {
  display: grid;
  grid-template-columns: 6px auto 1fr;
  column-gap: 9px;
  row-gap: 1px;
  align-items: baseline;
}
.nw-act-card-k .kr i {
  grid-row: 1 / span 2;
  align-self: start;
  width: 6px;
  height: 15px;
  margin-top: 2px;
  border-radius: 1.5px;
  background: var(--line2);
}
.nw-act-card-k .kr.is-in i { background: var(--tone); }
.nw-act-card-k .kl { color: var(--ink); font-weight: 500; }
.nw-act-card-k .kv {
  justify-self: end;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.nw-act-card-k .kn { grid-column: 2 / -1; font-size: 11.5px; }

.nw-act-card-f {
  padding-top: 9px;
  border-top: 1px solid var(--line);
  font-size: 11.5px;
}
.nw-act-card-go {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: -2px;
  font-family: var(--font-display);
  font-size: 12px;
  font-weight: 600;
  color: var(--accent);
}
.nw-act-card-go svg { flex: none; width: 14px; height: 14px; }

/* The notch, drawn on the wrapper rather than the card: it sits under the
   hour being read, which is a position along the SPARKLINE, and the card is
   wider than that. A square turned 45° with only its outer two edges
   ruled, so its inner half melts into the card. */
.nw-act-card-nub {
  position: absolute;
  top: calc(100% + 5px);
  z-index: 21;
  width: 10px;
  height: 10px;
  background: var(--card);
  border: 1px solid var(--line2);
  border-right: 0;
  border-bottom: 0;
  border-top-left-radius: 2px;
  transform: translateX(-50%) rotate(45deg);
  transition: left .09s ease-out;
  pointer-events: none;
  animation: nw-act-nub-in .14s ease-out both;
}
.nw-act-card-nub.is-above {
  top: auto;
  bottom: calc(100% + 5px);
  border: 1px solid var(--line2);
  border-left: 0;
  border-top: 0;
  border-radius: 0 0 2px 0;
}
@keyframes nw-act-nub-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.nw-act-div {
  flex: none;
  align-self: stretch;
  width: 1px;
  margin: 4px 0;
  background: var(--line);
}
.nw-act-go {
  flex: none;
  display: inline-flex;
  align-items: center;
  color: var(--accent);
}
.nw-act-go svg { width: 18px; height: 18px; transition: transform .15s cubic-bezier(.2, .8, .2, 1); }
.nw-act:hover .nw-act-go svg { transform: translateX(2px); }

@media (max-width: 720px) {
  /* The sparkline is the first thing to go: below this width it has fewer
     than 5px an hour, at which point it states a shape it cannot support. The
     counts it illustrates are still on the row. */
  .nw-act-sparkw, .nw-act-div { display: none; }
}
@media (max-width: 480px) {
  .nw-act { gap: 12px; padding: 13px 13px; align-items: flex-start; }
  .nw-act-gl { width: 40px; height: 40px; border-radius: 11px; }
  .nw-act-go { align-self: center; }
  /* Stacked, not wrapped. Left to wrap, the longest of these left the rule
     stranded at the end of the first line, dividing nothing. */
  .nw-act-s { flex-direction: column; align-items: flex-start; gap: 2px; }
  .nw-act-s .sep, .nw-act-s .dot { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .nw-act.is-live .nw-act-chip::before,
  .nw-act-go svg,
  .nw-act-bar,
  .nw-act-card,
  .nw-act-card-nub { animation: none; transition: none; }
}

/* Inside the dialog the card supplies its own chrome, so it drops the
   outer frame the Outlook page gives it. */
.nw-ol-dialog .nw-ac {
  background: none;
  border: 0;
  border-radius: 0;
  padding: 0;
  margin: 0;
}

/* --- feedback ----------------------------------------------------------- */

/* The fourth band, in the same panel as the other three. The question and its
   buttons share one line where the width allows it. */
.nw-acf {
  display: flex;
  align-items: center;
  gap: 12px 16px;
  flex-wrap: wrap;
}
.nw-acf-q { flex: 1 1 270px; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.nw-acf-t {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}
/* Not faint: this is the sentence that refuses the thing a reader would
   otherwise assume — that answering re-runs the analysis in front of them —
   so it is not the smallest thing in the band. */
.nw-acf-s { font-size: 11.5px; line-height: 1.45; color: var(--soft); max-width: 54ch; }
.nw-acf-btns { display: flex; gap: 7px; flex-wrap: wrap; }
.nw-acf-b {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--soft);
  background: var(--chip-bg);
  border: 1px solid var(--line);
  border-radius: 9px;
  padding: 7px 13px;
  cursor: pointer;
  transition: color .15s, border-color .15s, background .15s;
}
.nw-acf-i { display: inline-flex; line-height: 0; }
.nw-acf-b:hover { background: var(--hover); color: var(--ink); }
.nw-acf-b:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
/* The chosen verdict states which one it is, in the tone that verdict means.
   Only once chosen: colouring all three at rest would ask the reader to
   answer in the card's vocabulary rather than their own. */
.nw-acf-b.on { color: var(--ink); border-color: var(--ink); }
.nw-acf-b.v-yes.on { color: var(--green, #46b27e); border-color: currentColor; }
.nw-acf-b.v-partly.on { color: var(--amber, #ffa059); border-color: currentColor; }
.nw-acf-b.v-no.on { color: var(--red, #e45959); border-color: currentColor; }
/* Not a fourth verdict. It opens a box rather than answering the question, so
   it is set apart from the three that do. */
.nw-acf-note { border-style: dashed; }
/* Both take a whole row of the flex line: the note box and the acknowledgement
   arrive AFTER the question and its buttons, and squeezing either in beside
   them would put a textarea in a 200px column. */
.nw-acf-box { flex: 1 0 100%; display: flex; flex-direction: column; gap: 8px; }
.nw-acf-in {
  font: inherit;
  font-size: 12.5px;
  color: var(--ink);
  background: var(--code-bg);
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 8px 10px;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
}
.nw-acf-send {
  align-self: flex-start;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  background: var(--chip-bg);
  border: 1px solid var(--ink);
  border-radius: 8px;
  padding: 6px 13px;
  cursor: pointer;
}
.nw-acf-ack { flex: 1 0 100%; font-size: 11.5px; color: var(--soft); margin: 0; }
